import { createAsyncThunk } from '@reduxjs/toolkit';
import { logger, monitoring } from 'common/services';
import { isFeatureEnabled } from 'common/utils';

import { createCustomBackgroundId } from '../constants';
import { getUsedChars, isLowResolutionBackground, readCustomBackgroundIndex, writeCustomBackground } from '../services';
import { isBackgroundImageError, normaliseBackgroundImage } from '../utils';

import type { CustomBackgroundEntry } from '../services';
import type { BackgroundImageErrorCode } from '../utils';

/** Filename without its extension, trimmed to something that fits under a 72px tile. */
const toBackgroundName = (fileName: string) =>
  fileName
    .replace(/\.[^.]+$/, '')
    .trim()
    .slice(0, 40) || 'Background';

/**
 * Re-encodes a picked file and persists it.
 *
 * The only async step here is the canvas encode; storage itself is synchronous. **The write happens
 * before the thunk fulfils** — deliberately not in a listener — because the budget guards can refuse
 * it. Updating the slice first and persisting afterwards would leave the picker showing a background
 * that isn't actually stored and vanishes on reload.
 *
 * Deliberately does not touch the form: `useVideoBackgroundPicker` stays the single writer of
 * `backgroundImageId`/`isBlurEnabled`/`blurState`, so the two can't disagree. The picker selects the
 * returned id itself.
 */
export const importCustomBackground = createAsyncThunk<
  CustomBackgroundEntry,
  File,
  { rejectValue: BackgroundImageErrorCode }
>('customBackgrounds/import', async (file, { rejectWithValue }) => {
  /**
   * The flag again, at the write layer. It's already checked twice — `useVideoBackgroundPicker` hides
   * the entry point and `useBackgroundImageId` refuses to resolve a custom id — but both of those are
   * read paths in one bundle. This is the only place bytes land in `localStorage`, and a stale tab
   * left open across a config change, or anything dispatching the thunk directly, would otherwise
   * write a user's photo to a browser where the feature is meant to be off entirely.
   */
  if (!isFeatureEnabled('CustomVideoBackground')) {
    logger.warn('[importCustomBackground] Refused a custom background: the feature is disabled');

    return rejectWithValue('featureDisabled');
  }

  try {
    const { imageDataUrl, thumbnailDataUrl, sourceWidth, sourceHeight } = await normaliseBackgroundImage(file);

    const entry = writeCustomBackground(
      {
        id: createCustomBackgroundId(),
        name: toBackgroundName(file.name),
        createdAt: Date.now(),
        sourceWidth,
        sourceHeight,
      },
      imageDataUrl,
      thumbnailDataUrl
    );

    monitoring.trackAction('devices.video_background.custom_imported', {
      count: readCustomBackgroundIndex().length,
      chars: entry.chars,
      totalChars: getUsedChars(),
      // How often people upload something we have to upscale, which is the evidence for whether the
      // low-resolution notice earns its place.
      sourceWidth,
      sourceHeight,
      isLowResolution: isLowResolutionBackground(entry),
    });

    return entry;
  } catch (error) {
    if (!isBackgroundImageError(error)) {
      logger.error('[importCustomBackground] Unexpected failure adding a custom background', error);

      return rejectWithValue('storageFailed');
    }

    /**
     * Both refusals are tracked, and the distinction is the point: `custom_limit_reached` says the
     * cap is in the user's way, which is the evidence that would justify moving to an async store
     * (Cache API) and paying for the complexity that avoids. `custom_budget_exceeded` says the images
     * are bigger than the encoder quality assumes.
     */
    if (error.code === 'limitReached' || error.code === 'quotaExceeded') {
      const context = { count: readCustomBackgroundIndex().length, totalChars: getUsedChars() };
      const action =
        error.code === 'limitReached'
          ? 'devices.video_background.custom_limit_reached'
          : 'devices.video_background.custom_budget_exceeded';

      logger.warn(`[importCustomBackground] Refused a custom background: ${error.code}`, context);
      monitoring.trackAction(action, context);
    } else {
      monitoring.trackAction('devices.video_background.custom_import_failed', { code: error.code });
    }

    return rejectWithValue(error.code);
  }
});
